import { useFilters } from "context/FilterContext"
import useEmblaCarousel from "embla-carousel-react"
import { WheelGesturesPlugin } from "embla-carousel-wheel-gestures"
import styled, { css } from "styled-components"
import {
  defaultThemeColors,
  mediaSizes,
  narrowContainerPaddingDesktop,
  narrowContainerPaddingMobile,
  narrowContainerWidth,
  responsivePaddingX,
} from "ui/theme"
import {
  NextButton,
  PrevButton,
  useEmblaKeyboard,
  usePrevNextButtons,
} from "../Button/EmblaButtons"
import { ClassCard } from "./ClassCard"
import {useEffect, useRef, useState} from "react"

export const ClassCarousel = ({
  slides,
  options,
  title,
  onSlideClick,
  narrowTitleContainer = false,
}) => {
  const { filters } = useFilters()
  const emblaRootRef = useRef(null)
  const [emblaRef, emblaApi] = useEmblaCarousel(options, [
    WheelGesturesPlugin(),
  ])
  const [isActive, setIsActive] = useState(false)
  const [leftOffset, setLeftOffset] = useState(0)

  const online_type = filters.online_type?.value || ""

  const handleActivate = () => {
    setIsActive(true)
  }

  const handleDeactivate = () => {
    setIsActive(false)
  }

  const {
    prevBtnDisabled,
    nextBtnDisabled,
    onPrevButtonClick,
    onNextButtonClick,
  } = usePrevNextButtons(emblaApi)

  useEmblaKeyboard(emblaApi, isActive)

  useEffect(() => {
    if (!narrowTitleContainer) return

    const updateLeftOffset = () => {
      const nextOffset = (emblaRootRef.current?.getBoundingClientRect().left || 0) + 16
      setLeftOffset(nextOffset)
    }

    updateLeftOffset()
    window.addEventListener("resize", updateLeftOffset)

    return () => {
      window.removeEventListener("resize", updateLeftOffset)
    }
  }, [narrowTitleContainer])

  return (
    <Embla
      ref={emblaRootRef}
      tabIndex={0}
      onMouseEnter={handleActivate}
      onMouseLeave={handleDeactivate}
      onFocus={handleActivate}
      onBlur={handleDeactivate}>
      <EmblaControls $narrow={narrowTitleContainer}>
        <EmblaTitle>{title}</EmblaTitle>
        <EmblaButtons>
          <PrevButton
            onClick={onPrevButtonClick}
            disabled={prevBtnDisabled}
          />
          <NextButton
            onClick={onNextButtonClick}
            disabled={nextBtnDisabled}
          />
        </EmblaButtons>
      </EmblaControls>

      <EmblaViewport
        ref={emblaRef}
        $narrow={narrowTitleContainer}>
        {narrowTitleContainer ? (
          <NarrowEmblaContainer $endGap={leftOffset}>
            {slides?.map((item, index) => (
              <EmblaSlide key={index}>
                <ClassCard
                  classData={item}
                  onClick={() => onSlideClick?.(item.user_id)}
                  online_type={online_type}
                />
              </EmblaSlide>
            ))}
          </NarrowEmblaContainer>
        ) : (
          <EmblaContainer>
            {slides?.map((item, index) => (
              <EmblaSlide key={index}>
                <ClassCard
                  classData={item}
                  onClick={() => onSlideClick?.(item.user_id)}
                  online_type={online_type}
                />
              </EmblaSlide>
            ))}
          </EmblaContainer>
        )}
      </EmblaViewport>
    </Embla>
  )
}

const Embla = styled.section`
  max-width: 100%;
  margin: auto;
`

const EmblaViewport = styled.div`
  width: 100%;
  overflow: hidden;
  padding: ${({ $narrow }) =>
    $narrow
      ? "0 0 56px"
      : `0 ${responsivePaddingX.desktop} 56px ${responsivePaddingX.desktop}`};
  box-sizing: border-box;

  ${({ $narrow }) =>
    !$narrow &&
    css`
      @media (max-width: ${mediaSizes.lg}) {
        padding: 0 ${responsivePaddingX.lg} 56px ${responsivePaddingX.lg};
      }

      @media (max-width: ${mediaSizes.md}) {
        padding: 0 ${responsivePaddingX.md} 56px ${responsivePaddingX.md};
      }

      @media (max-width: ${mediaSizes.xs}) {
        padding: 0 ${responsivePaddingX.xs} 56px ${responsivePaddingX.xs};
      }
    `}
`

const EmblaContainer = styled.div`
  display: flex;
  touch-action: pan-y pinch-zoom;
  margin-left: calc(24px * -1);
  margin-right: calc(${responsivePaddingX.desktop} - 24px);
  will-change: transform;
  backface-visibility: hidden;
`

const NarrowEmblaContainer = styled(EmblaContainer)`
  margin-right: ${({ $endGap = 0 }) => `calc(${$endGap}px - 24px)`};
`

const EmblaSlide = styled.div`
  flex: 0 0 320px;
  min-width: 0;
  padding-left: 24px;
  @media (width >= ${mediaSizes.xs}) {
    flex: 0 0 344px;
  }
`

const EmblaControls = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-sizing: border-box;
  gap: 1.2rem;
  margin-bottom: 2px;

  ${({ $narrow }) =>
    $narrow
      ? css`
          width: 100%;
          padding: 56px ${narrowContainerPaddingMobile} 32px 0;

          @media (width >= ${mediaSizes.md}) {
            padding: 56px ${narrowContainerPaddingDesktop} 32px 0;
            width: 100%;
          }
            
          @media (width >= ${mediaSizes.xl}) {
            width: calc(${narrowContainerWidth} - 2 * ${narrowContainerPaddingDesktop});
            padding: 56px 0 32px 0;
          }
        `
      : css`
          width: 100%;
          padding: 56px ${responsivePaddingX.desktop} 32px ${responsivePaddingX.desktop};

          @media (max-width: ${mediaSizes.lg}) {
            padding: 56px ${responsivePaddingX.lg} 32px ${responsivePaddingX.lg};
          }

          @media (max-width: ${mediaSizes.md}) {
            padding: 56px ${responsivePaddingX.md} 32px ${responsivePaddingX.md};
          }

          @media (max-width: ${mediaSizes.xs}) {
            padding: 56px ${responsivePaddingX.xs} 32px ${responsivePaddingX.xs};
          }
        `
    }
`
const EmblaButtons = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.6rem;
  align-items: center;
`
const EmblaTitle = styled.div`
  font-size: 32px;
  font-weight: 500;
  color: ${defaultThemeColors.basicBlack};
  margin: 0;
  line-height: 1.2;

  @media (max-width: ${mediaSizes.lg}) {
    font-size: 28px;
  }

  @media (max-width: ${mediaSizes.md}) {
    font-size: 26px;
  }

  @media (max-width: ${mediaSizes.xs}) {
    font-size: 22px;
  }
`
